import { Component, computed, input, output } from '@angular/core';
import { NgClass } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';

import { TranslateModule } from '@ngx-translate/core';
import { AvatarComponent } from '@shared/components/ui/avatar';
import { BadgeComponent } from '@shared/components/ui/badge';
import { ErrorStateComponent } from '@shared/components/ui/error-state';
import { SkeletonLoaderComponent } from '@shared/components/ui/skeleton-loader';
import type { WaitingStudent } from '@live-pickup/data-access';

export type Priority = 'normal' | 'attention' | 'critical';

interface PriorityConfig {
  border: string;
  bg: string;
  indicator: string;
  text: string;
}

@Component({
  selector: 'lib-waiting-list',
  standalone: true,
  imports: [NgClass, AvatarComponent, BadgeComponent, ErrorStateComponent, SkeletonLoaderComponent, TranslateModule],
  templateUrl: './waiting-list.component.html',
  styleUrl: './waiting-list.component.scss',
  animations: [
    trigger('removeCheckin', [
      transition(':leave', [
        style({ height: '94px', overflow: 'hidden', transform: 'translateX(0)', opacity: 1, position: 'relative', marginBottom: 0 }),
        animate(
          '700ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ height: '94px', opacity: 0, transform: 'translateX(100%)' })
        ),
        animate(
          '500ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ height: '0px', marginBottom: '-10px' })
        ),
      ]),
    ]),
  ],
})
export class WaitingListComponent {
  readonly students = input.required<WaitingStudent[]>();
  readonly highlightedIds = input<string[]>([]);
  readonly hasError = input(false);
  readonly isLoading = input(false);
  readonly retry = output<void>();

  readonly skeletonRows = [0, 1, 2, 3];

  readonly priorityCounts = computed(() => {
    const counts = { normal: 0, attention: 0, critical: 0 };
    for (const student of this.students()) {
      counts[this.getPriority(student.waitSeconds)]++;
    }
    return counts;
  });

  getPriority(seconds: number): Priority {
    if (seconds > 360) return 'critical';
    if (seconds > 180) return 'attention';
    return 'normal';
  }

  getPriorityConfig(priority: Priority): PriorityConfig {
    const configs: Record<Priority, PriorityConfig> = {
      normal: {
        border: 'border-border-solid',
        bg: 'bg-[color-mix(in_srgb,var(--secondary)_70%,transparent)]',
        indicator: 'bg-muted-foreground',
        text: 'text-foreground',
      },
      attention: {
        border: 'border-[color-mix(in_srgb,var(--warning)_40%,transparent)]',
        bg: 'bg-[color-mix(in_srgb,var(--warning-fade)_80%,transparent)]',
        indicator: 'bg-warning',
        text: 'text-warning-foreground',
      },
      critical: {
        border: 'border-[color-mix(in_srgb,var(--danger)_50%,transparent)]',
        bg: 'bg-[color-mix(in_srgb,var(--danger-fade)_90%,transparent)]',
        indicator: 'bg-danger animate-pulse',
        text: 'text-danger-foreground',
      },
    };
    return configs[priority];
  }
}
